import request from '../request';
import services from '../../services';
import { STATE, GMI} from '../../constants';
import { isArrayNotEmpty } from '../../utils/js/array';
import { batchActions } from '../../utils/redux';


export const { EMERALD_CHECKOUT } = STATE.GMA;

/**
 * @private
 * @param {Object} ticket
 * @returns {Object} {type: string, path: [], value: string}
 */
function setEmeraldCheckoutInitiate(ticket) {
  return {type: GMI.SET_GMI_STATE, path: EMERALD_CHECKOUT.INITIATE, value: ticket};
}

/**
 * @private
 * @param {Object} ticket
 * @returns {Object} {type: string, path: [], value: string}
 */
function setEmeraldCheckoutCommit(ticket) {
  return {type: GMI.SET_GMI_STATE, path: EMERALD_CHECKOUT.COMMIT, value: ticket};
}

/**
 * @private
 * @returns {Object} {type: string, path: [], value: string}
 */
function clearEmeraldCheckoutInitiate() {
  return {type: GMI.DELETE_GMI_STATE, path: EMERALD_CHECKOUT.INITIATE, value: {}};
}

/**
 * @private
 * @returns {Object} {type: string, path: [], value: string}
 */
function clearEmeraldCheckoutCommit() {
  return {type: GMI.DELETE_GMI_STATE, path: EMERALD_CHECKOUT.COMMIT, value: {}};
}

/**
 * @private
 * @param {Object} vehicles
 * @returns {Object} {type: string, path: [], value: string}
 */
function setEmeraldCheckoutAvailableVehiclesById(vehicles) {
  return {type: GMI.SET_GMI_STATE, path: EMERALD_CHECKOUT.VEHICLES.BY_SESSION_ID, value: vehicles};
}

/**
 * @name initiateEmeraldCheckout
 * @desc Make the GMA /reservations/checkout/${confirmationNumber}/initiate call and update the state
 * @param {String} [confirmationNumber]
 * @param {Object} [data] {params}
 * @param {Object} [requestOpts]
 * @returns {Object}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.initiateEmeraldCheckout(data));
 */
export function initiateEmeraldCheckout(data, requestOpts = {}) {
  return (dispatch) => {
    const { params, payload } = data;
    return dispatch(request({
      service: services.gma.reservations.initiateEmeraldCheckout,
      data: {params, payload},
      opts: requestOpts
    })).then(resp => {
      if (!isArrayNotEmpty(resp.messages)) {
        dispatch(setEmeraldCheckoutInitiate(resp.ticket));
        return resp.ticket;
      } else {
        return resp;
      }
    });
  };
}
/**
 * @name selectEmeraldCheckoutVehicle
 * @desc Make the GMA /reservations/checkout/${ticketSessionId}/selectVehicle call and update the state
 * @param {String} [ticketSessionId]
 * @param {String} [vin]
 * @param {Object} [data] {params}
 * @param {Object} [requestOpts]
 * @returns {Object}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.selectEmeraldCheckoutVehicle(data));
 */
export function selectEmeraldCheckoutVehicle(data, requestOpts = {}) {
  return (dispatch) => {
    const { params, payload } = data;
    return dispatch(request({
      service: services.gma.reservations.selectEmeraldCheckoutVehicle,
      data: {params, payload},
      opts: requestOpts
    })).then(resp => {
      if (!isArrayNotEmpty(resp.messages)) {
        dispatch(setEmeraldCheckoutInitiate(resp.ticket));
        return resp.ticket;
      } else {
        return resp;
      }
    });
  };
}

/**
 * @name updateEmeraldCheckoutProducts
 * @desc Make the GMA /reservations/checkout/${ticketSessionId}/updateProducts call and update the state
 * @param {String} [ticketSessionId]
 * @param {String} [vin]
 * @param {Object} [data] {params}
 * @param {Object} [requestOpts]
 * @returns {Object}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.updateEmeraldCheckoutProducts(data));
 */
export function updateEmeraldCheckoutProducts(data, requestOpts = {}) {
  return (dispatch) => {
    const { params, payload } = data;
    return dispatch(request({
      service: services.gma.reservations.updateEmeraldCheckoutProducts,
      data: {params, payload},
      opts: requestOpts
    })).then(resp => {
      if (!isArrayNotEmpty(resp.messages)) {
        dispatch(setEmeraldCheckoutInitiate(resp.ticket));
        return resp.ticket;
      } else {
        return resp;
      }
    });
  };
}

/**
 * @name updateEmeraldCheckoutPaymentMethod
 * @desc Make the GMA /reservations/checkout/${ticketSessionId}/updatePayment call and update the state
 * @param {String} [ticketSessionId]
 * @param {Array} [payment_ids]
 * @param {Object} [data] {params}
 * @param {Object} [requestOpts]
 * @returns {Object}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.updateEmeraldCheckoutPaymentMethod(data));
 */
export function updateEmeraldCheckoutPaymentMethod(data, requestOpts = {}) {
  return (dispatch) => {
    const { params, payload } = data;
    return dispatch(request({
      service: services.gma.reservations.updateEmeraldCheckoutPaymentMethod,
      data: {params, payload},
      opts: requestOpts
    })).then(resp => {
      if (!isArrayNotEmpty(resp.messages)) {
        dispatch(setEmeraldCheckoutInitiate(resp.ticket));
        return resp.ticket;
      } else {
        return resp;
      }
    });
  };
}

/**
 * @name commitEmeraldCheckout
 * @desc Make the GMA /reservations/checkout/${ticketSessionId}/commit call and update the state
 * @param {Object} [data] {params}
 * @param {string} [ticketSessionId]
 * @param {Object} [additional_infos] // Only needed if split pay
 * @param {Object} [payment_ids] // Only needed if additional info is required to complete ticket
 * @returns {Object}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.commitEmeraldCheckout(data));
 */
export function commitEmeraldCheckout(data, requestOpts = {}) {
  return (dispatch) => {
    const { params, payload } = data;
    return dispatch(request({
      service: services.gma.reservations.commitEmeraldCheckout,
      data: {params, payload},
      opts: requestOpts
    })).then(resp => {
      if (!isArrayNotEmpty(resp.messages)) {
        dispatch(setEmeraldCheckoutCommit(resp.ticket));
        return resp.ticket;
      } else {
        return resp;
      }
    });
  };
}

/**
 * @name clearEmeraldCheckoutState
 * @desc Clear the Emerald Checkout state use opts as false if some state
 * doesn't need to be cleared
 * @param {Object} [opts]
 * @returns {Object}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.clearEmeraldCheckoutState(opts));
 * const opts = {
 *     initiate: true,
 *     commit: false
 * }
 */
export function clearEmeraldCheckoutState(opts = {}) {
  return (dispatch) => {
    const {
      initiate = true,
      commit = true
    } = opts;
    const batchedActions = [];
    if (initiate) batchedActions.push(clearEmeraldCheckoutInitiate());
    if (commit) batchedActions.push(clearEmeraldCheckoutCommit());
    if (batchedActions.length !== 0) dispatch(batchActions(...batchedActions));
  };
}
/**
 * @name listEmeraldCheckoutVehicle
 * @desc Make the GMA /reservations/checkout/${ticketSessionId}/getAvailableVehicles call
 * @param {String} [ticketSessionId]
 * @param {Object} [requestOpts]
 * @returns {Object}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.listEmeraldCheckoutVehicle(data));
 */
export function listEmeraldCheckoutVehicle(data, requestOpts = {}) {
  return (dispatch, getState) => {
    const { params, payload } = data;
    const { ticketSessionId } = payload;
    let vehicles = getState().getIn(EMERALD_CHECKOUT.VEHICLES.BY_SESSION_ID).toJS();

    return dispatch(request({
      service: services.gma.reservations.listEmeraldCheckoutVehicle,
      data: {params, payload},
      opts: requestOpts
    })).then(resp => {
      if (!isArrayNotEmpty(resp.messages)) {

        const newVehicles = {
          [ticketSessionId]: resp.vehicles
        };
        vehicles = Object.assign({}, vehicles,  newVehicles);
        dispatch(setEmeraldCheckoutAvailableVehiclesById(vehicles));
        return vehicles;
      } else {
        return resp;
      }
    });
  };
}
/**
 * @name cancelEmeraldCheckoutVehicle
 * @desc Make the GMA /reservations/checkout/cancelVehicle call
 * @param {Object} [data] {params}
 * @param {string} [ticket_number]
 * @returns {Object}
 * @memberof actions.reservations
 * @example
 * store.dispatch(actions.reservations.cancelEmeraldCheckoutVehicle(data));
 */
export function cancelEmeraldCheckoutVehicle(data, requestOpts = {}) {
  return (dispatch) => {
    const { params, payload } = data;
    return dispatch(request({
      service: services.gma.reservations.cancelEmeraldCheckoutVehicle,
      data: {params, payload},
      opts: requestOpts
    })).then(resp => resp);
  };
}
